export type ChatRole = 'user' | 'assistant'

export interface ChatMessage {
  role: ChatRole
  content: string
}

const HISTORY_PREFIX = 'kc3-client-ai-chat:'

export function historyKey(tenantSlug: string): string {
  return `${HISTORY_PREFIX}${tenantSlug}`
}

const VALID_ROLES: ReadonlySet<string> = new Set(['user', 'assistant'])

/**
 * Validates a single parsed message object. Guards against stale/corrupted
 * localStorage data that could cause API 400 errors (empty content, invalid
 * roles, non-string fields).
 */
function isValidMessage(m: unknown): m is ChatMessage {
  if (typeof m !== 'object' || m === null) return false
  const msg = m as { role?: unknown; content?: unknown }
  return (
    typeof msg.role === 'string' &&
    VALID_ROLES.has(msg.role) &&
    typeof msg.content === 'string' &&
    msg.content.length > 0
  )
}

export function loadHistory(tenantSlug: string): ChatMessage[] {
  try {
    const raw = localStorage.getItem(historyKey(tenantSlug))
    if (!raw) return []
    const parsed = JSON.parse(raw) as unknown
    if (!Array.isArray(parsed)) return []
    // Filter out any corrupted/stale entries instead of rejecting the whole
    // array — a single bad entry (e.g. empty content from a failed stream)
    // should not break the entire conversation history.
    return parsed.filter(isValidMessage)
  } catch {
    // ignore
  }
  return []
}

export function saveHistory(tenantSlug: string, messages: ChatMessage[]): void {
  try {
    localStorage.setItem(historyKey(tenantSlug), JSON.stringify(messages))
  } catch {
    // ignore
  }
}

const ESCALATION_PREFIX = 'kc3-client-ai-chat-escalation:'

export function escalationKey(tenantSlug: string): string {
  return `${ESCALATION_PREFIX}${tenantSlug}`
}

const SESSION_PREFIX = 'kc3-client-ai-chat-session:'

export function sessionKey(tenantSlug: string): string {
  return `${SESSION_PREFIX}${tenantSlug}`
}

export function loadSessionId(tenantSlug: string): string | null {
  try {
    return localStorage.getItem(sessionKey(tenantSlug))
  } catch {
    return null
  }
}

export function saveSessionId(tenantSlug: string, sessionId: string): void {
  try {
    localStorage.setItem(sessionKey(tenantSlug), sessionId)
  } catch {
    // ignore
  }
}

export function clearSessionId(tenantSlug: string): void {
  try {
    localStorage.removeItem(sessionKey(tenantSlug))
  } catch {
    // ignore
  }
}

export function loadEscalationFlag(tenantSlug: string): boolean {
  try {
    return localStorage.getItem(escalationKey(tenantSlug)) === 'true'
  } catch {
    return false
  }
}

export function saveEscalationFlag(tenantSlug: string, value: boolean): void {
  try {
    localStorage.setItem(escalationKey(tenantSlug), value ? 'true' : 'false')
  } catch {
    // ignore
  }
}

// Prefixes for all client-portal-related localStorage keys that must be
// cleared on logout. Grouped here so the logout path never misses one.
const GUEST_IDENTITY_PREFIX = 'kc3-client-guest-identity:'
const WIDGET_CONVERSATION_PREFIX = 'kc3-widget-conversation:'
const WIDGET_MSGS_PREFIX = 'kc3-widget-msgs:'

const CLEARABLE_PREFIXES = [
  HISTORY_PREFIX,
  ESCALATION_PREFIX,
  SESSION_PREFIX,
  GUEST_IDENTITY_PREFIX,
  WIDGET_CONVERSATION_PREFIX,
  WIDGET_MSGS_PREFIX,
] as const

/**
 * Clears ALL client-portal chat-related localStorage: AI chat history,
 * escalation flags, guest identity, widget conversation mappings, and
 * widget cached messages. Called on client logout so a returning visitor
 * starts fresh instead of seeing the previous session's stale chat.
 */
export function clearClientAiChatHistory(): void {
  try {
    for (const key of Object.keys(localStorage)) {
      if (CLEARABLE_PREFIXES.some((prefix) => key.startsWith(prefix))) {
        localStorage.removeItem(key)
      }
    }
  } catch {
    // ignore
  }
}
